import { useEffect, useState } from 'react'

/**
 * The width at or below which the account rail collapses into an off-canvas
 * drawer. Must stay in step with the `.wh-sidebar` block in `components.css` —
 * the stylesheet does the collapsing, this constant only tells the shells and
 * the top bars where it happened.
 *
 * 1024 rather than the 768 phone breakpoint because every iPad in portrait
 * (768-1024pt) sits above 768, and the inline 248px rail squeezes the content
 * column hard enough there to clip wide surfaces like the hunts calendar.
 */
export const NAV_DRAWER_MAX = 1024

/**
 * Whether the rail is currently a drawer.
 *
 * The shells need this in JS, not only in CSS: the open/closed state is React
 * state, and a stylesheet can hide the drawer but cannot reset that state.
 *
 * Both `change` and `resize` are observed, and each re-reads `matches` rather
 * than trusting an event payload — under device emulation the viewport can
 * cross the breakpoint without a `change` firing.
 *
 * `matchMedia` is feature-detected rather than assumed: it is absent under SSR
 * and in the jsdom build the component tests run on, where falling back to the
 * desktop layout is the right answer.
 */
export function useHasNavDrawer() {
  const query = `(max-width: ${NAV_DRAWER_MAX}px)`
  const [matches, setMatches] = useState(() => matchQuery(query))

  useEffect(() => {
    const mq = typeof window !== 'undefined' ? window.matchMedia?.(query) : undefined
    if (!mq) return
    const sync = () => setMatches(mq.matches)
    sync()
    window.addEventListener('resize', sync)
    // Safari below 14 only has the deprecated listener pair.
    if (mq.addEventListener) mq.addEventListener('change', sync)
    else mq.addListener(sync)
    return () => {
      window.removeEventListener('resize', sync)
      if (mq.removeEventListener) mq.removeEventListener('change', sync)
      else mq.removeListener(sync)
    }
  }, [query])

  return matches
}

function matchQuery(query: string) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(query).matches
}
